import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Typography,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    ListItemSecondaryAction,
    Avatar,
    Chip,
    Alert,
    CircularProgress,
    Paper,
    Divider,
    Tabs,
    Tab,
    Tooltip,
    Menu
} from '@mui/material';
import {
    ArrowBack as BackIcon,
    Add as AddIcon,
    MoreVert as MoreVertIcon,
    Person as PersonIcon,
    AdminPanelSettings as AdminIcon,
    Visibility as ViewerIcon,
    PersonAdd as InviteIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Email as EmailIcon,
    CheckCircle as AcceptIcon,
    Cancel as DeclineIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface ProjectMember {
    id: number;
    role: 'owner' | 'admin' | 'collaborator' | 'viewer' | 'client';
    status: 'pending' | 'accepted' | 'declined';
    invitedAt: string;
    joinedAt?: string;
    user: {
        id: number;
        name: string;
        email: string;
    };
    inviter: {
        id: number;
        name: string;
        email: string;
    };
}

interface ProjectInvitation {
    id: number;
    email: string;
    role: 'admin' | 'collaborator' | 'viewer' | 'client';
    invitedAt: string;
    expiresAt: string;
    inviter: {
        id: number;
        name: string;
        email: string;
    };
}

interface Project {
    id: number;
    name: string;
    description: string;
    color: string;
    icon: string;
    owner: {
        id: number;
        name: string;
        email: string;
    };
    role: 'owner' | 'admin' | 'collaborator' | 'viewer' | 'client';
}

interface InviteData {
    email: string;
    role: 'admin' | 'collaborator' | 'viewer' | 'client';
}

const ProjectMembers: React.FC = () => {
    const { projectId } = useParams<{ projectId: string }>();
    const navigate = useNavigate();
    const { user } = useAuth();

    const [project, setProject] = useState<Project | null>(null);
    const [members, setMembers] = useState<ProjectMember[]>([]);
    const [invitations, setInvitations] = useState<ProjectInvitation[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState(0);
    const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const [selectedMember, setSelectedMember] = useState<ProjectMember | null>(null);
    const [inviting, setInviting] = useState(false);

    const [inviteData, setInviteData] = useState<InviteData>({
        email: '',
        role: 'collaborator'
    });

    const roleColors = {
        owner: '#EF4444',
        admin: '#F59E0B',
        collaborator: '#3B82F6',
        viewer: '#6B7280',
        client: '#8B5CF6'
    };

    const roleIcons = {
        owner: <AdminIcon />,
        admin: <AdminIcon />,
        collaborator: <PersonIcon />,
        viewer: <ViewerIcon />,
        client: <PersonIcon />
    };

    const roleNames = {
        owner: 'Propietario',
        admin: 'Administrador',
        collaborator: 'Colaborador',
        viewer: 'Solo lectura',
        client: 'Cliente'
    };

    useEffect(() => {
        if (projectId) {
            fetchProjectData();
        }
    }, [projectId]);

    const fetchProjectData = async () => {
        try {
            setLoading(true);
            const [projectResponse, membersResponse, invitationsResponse] = await Promise.all([
                axios.get(`/api/tasks/projects/${projectId}`),
                axios.get(`/api/tasks/projects/${projectId}/members`),
                axios.get(`/api/tasks/projects/${projectId}/invitations`)
            ]);

            setProject(projectResponse.data);
            setMembers(membersResponse.data);
            setInvitations(invitationsResponse.data);
            setError(null);
        } catch (err: any) {
            console.error('Error fetching project data:', err);
            setError(err.response?.data?.error || 'Failed to load project data');
        } finally {
            setLoading(false);
        }
    };

    const handleInvite = async () => {
        if (!inviteData.email.trim()) {
            setError('Email is required');
            return;
        }

        try {
            setInviting(true);
            await axios.post(`/api/tasks/projects/${projectId}/invite`, inviteData);

            // Refresh invitations
            const response = await axios.get(`/api/tasks/projects/${projectId}/invitations`);
            setInvitations(response.data);

            setInviteDialogOpen(false);
            setInviteData({ email: '', role: 'collaborator' });
            setError(null);
        } catch (err: any) {
            console.error('Error inviting user:', err);
            setError(err.response?.data?.error || 'Failed to invite user');
        } finally {
            setInviting(false);
        }
    };

    const handleUpdateMemberRole = async (memberId: number, newRole: string) => {
        try {
            await axios.put(`/api/tasks/projects/${projectId}/members/${memberId}`, { role: newRole });

            setMembers(prev => prev.map(member =>
                member.id === memberId ? { ...member, role: newRole as any } : member
            ));

            setMenuAnchor(null);
            setSelectedMember(null);
        } catch (err: any) {
            console.error('Error updating member role:', err);
            setError(err.response?.data?.error || 'Failed to update member role');
        }
    };

    const handleRemoveMember = async (memberId: number) => {
        try {
            await axios.delete(`/api/tasks/projects/${projectId}/members/${memberId}`);

            setMembers(prev => prev.filter(member => member.id !== memberId));

            setMenuAnchor(null);
            setSelectedMember(null);
        } catch (err: any) {
            console.error('Error removing member:', err);
            setError(err.response?.data?.error || 'Failed to remove member');
        }
    };

    const handleRevokeInvitation = async (invitationId: number) => {
        try {
            await axios.delete(`/api/tasks/projects/${projectId}/invitations/${invitationId}`);

            setInvitations(prev => prev.filter(invitation => invitation.id !== invitationId));

            setMenuAnchor(null);
            setSelectedMember(null);
        } catch (err: any) {
            console.error('Error revoking invitation:', err);
            setError(err.response?.data?.error || 'Failed to revoke invitation');
        }
    };

    const openMemberMenu = (event: React.MouseEvent<HTMLElement>, member: ProjectMember) => {
        event.stopPropagation();
        setMenuAnchor(event.currentTarget);
        setSelectedMember(member);
    };

    const canManageMembers = project?.role === 'owner' || project?.role === 'admin';
    const isOwner = project?.role === 'owner';

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    if (error && !project) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Alert severity="error">
                    {error}
                    <Button onClick={() => navigate('/tasks')} sx={{ ml: 2 }}>
                        Volver
                    </Button>
                </Alert>
            </Container>
        );
    }

    if (!project) return null;

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                <IconButton onClick={() => navigate(`/tasks/project/${projectId}`)} sx={{ mr: 2 }}>
                    <BackIcon />
                </IconButton>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="h4" component="h1" gutterBottom>
                        Miembros del Proyecto
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {project.name}
                    </Typography>
                </Box>
                {canManageMembers && (
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setInviteDialogOpen(true)}
                    >
                        Invitar Usuario
                    </Button>
                )}
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Tabs */}
            <Paper sx={{ mb: 3 }}>
                <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
                    <Tab label={`Miembros (${members.length})`} />
                    <Tab label={`Invitaciones (${invitations.length})`} />
                </Tabs>
            </Paper>

            {/* Members Tab */}
            {activeTab === 0 && (
                <Paper>
                    <List>
                        {members.map((member, index) => (
                            <React.Fragment key={member.id}>
                                <ListItem>
                                    <ListItemAvatar>
                                        <Avatar>
                                            {member.user.name.charAt(0)}
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="subtitle1">
                                                    {member.user.name}
                                                </Typography>
                                                {member.status === 'pending' && (
                                                    <Chip label="Pendiente" size="small" color="warning" />
                                                )}
                                                {user?.id && user.id === String(member.user.id) && (
                                                    <Chip label="Tú" size="small" color="primary" />
                                                )}
                                            </Box>
                                        }
                                        secondary={
                                            <Box>
                                                <Typography variant="body2" color="text.secondary">
                                                    {member.user.email}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Invitado por {member.inviter.name} el{' '}
                                                    {format(new Date(member.invitedAt), 'dd MMM yyyy', { locale: es })}
                                                    {member.joinedAt && (
                                                        <>
                                                            {' • Se unió el '}
                                                            {format(new Date(member.joinedAt), 'dd MMM yyyy', { locale: es })}
                                                        </>
                                                    )}
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                    <ListItemSecondaryAction>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Chip
                                                icon={roleIcons[member.role]}
                                                label={roleNames[member.role]}
                                                size="small"
                                                sx={{
                                                    bgcolor: roleColors[member.role],
                                                    color: 'white'
                                                }}
                                            />
                                            {canManageMembers && (
                                                <IconButton onClick={(e) => openMemberMenu(e, member)}>
                                                    <MoreVertIcon />
                                                </IconButton>
                                            )}
                                        </Box>
                                    </ListItemSecondaryAction>
                                </ListItem>
                                {index < members.length - 1 && <Divider />}
                            </React.Fragment>
                        ))}
                    </List>
                </Paper>
            )}

            {/* Invitations Tab */}
            {activeTab === 1 && (
                <Paper>
                    <List>
                        {invitations.map((invitation, index) => (
                            <React.Fragment key={invitation.id}>
                                <ListItem>
                                    <ListItemAvatar>
                                        <Avatar sx={{ bgcolor: 'grey.400' }}>
                                            <EmailIcon />
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={
                                            <Typography variant="subtitle1">
                                                {invitation.email}
                                            </Typography>
                                        }
                                        secondary={
                                            <Box>
                                                <Typography variant="body2" color="text.secondary">
                                                    Invitado por {invitation.inviter.name} el{' '}
                                                    {format(new Date(invitation.invitedAt), 'dd MMM yyyy', { locale: es })}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Vence el {format(new Date(invitation.expiresAt), 'dd MMM yyyy', { locale: es })}
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                    <ListItemSecondaryAction>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Chip
                                                label={roleNames[invitation.role]}
                                                size="small"
                                                sx={{
                                                    bgcolor: roleColors[invitation.role],
                                                    color: 'white'
                                                }}
                                            />
                                            {canManageMembers && (
                                                <IconButton
                                                    onClick={() => handleRevokeInvitation(invitation.id)}
                                                    color="error"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            )}
                                        </Box>
                                    </ListItemSecondaryAction>
                                </ListItem>
                                {index < invitations.length - 1 && <Divider />}
                            </React.Fragment>
                        ))}
                    </List>
                    {
                        invitations.length === 0 && (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                <Typography variant="body2" color="text.secondary">
                                    No hay invitaciones pendientes
                                </Typography>
                            </Box>
                        )
                    }
                </Paper>
            )}

            {/* Member Menu */}
            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => {
                    setMenuAnchor(null);
                    setSelectedMember(null);
                }}
            >
                {selectedMember && selectedMember.role !== 'owner' && (
                    <>
                        <MenuItem onClick={() => handleUpdateMemberRole(selectedMember.id, 'admin')}>
                            <AdminIcon sx={{ mr: 1 }} />
                            Hacer Administrador
                        </MenuItem>
                        <MenuItem onClick={() => handleUpdateMemberRole(selectedMember.id, 'collaborator')}>
                            <PersonIcon sx={{ mr: 1 }} />
                            Hacer Colaborador
                        </MenuItem>
                        <MenuItem onClick={() => handleUpdateMemberRole(selectedMember.id, 'viewer')}>
                            <ViewerIcon sx={{ mr: 1 }} />
                            Solo Lectura
                        </MenuItem>
                        <Divider />
                        <MenuItem
                            onClick={() => handleRemoveMember(selectedMember.id)}
                            sx={{ color: 'error.main' }}
                        >
                            <DeleteIcon sx={{ mr: 1 }} />
                            Remover del Proyecto
                        </MenuItem>
                    </>
                )}
            </Menu>

            {/* Invite Dialog */}
            <Dialog open={inviteDialogOpen} onClose={() => setInviteDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Invitar Usuario al Proyecto</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <TextField
                            fullWidth
                            label="Email del usuario"
                            type="email"
                            value={inviteData.email}
                            onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                            margin="normal"
                            required
                        />
                        <FormControl fullWidth margin="normal">
                            <InputLabel>Rol</InputLabel>
                            <Select
                                value={inviteData.role}
                                onChange={(e) => setInviteData({ ...inviteData, role: e.target.value as any })}
                                label="Rol"
                            >
                                <MenuItem value="collaborator">Colaborador</MenuItem>
                                <MenuItem value="viewer">Solo lectura</MenuItem>
                                <MenuItem value="admin">Administrador</MenuItem>
                                <MenuItem value="client">Cliente</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setInviteDialogOpen(false)}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleInvite}
                        variant="contained"
                        disabled={inviting || !inviteData.email.trim()}
                    >
                        {inviting ? <CircularProgress size={20} /> : 'Enviar Invitación'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default ProjectMembers;